import { TextareaAutosize } from '@mui/material'
import { t } from 'i18next'
import React, { useEffect, useState } from 'react'
import { NotificationManager } from 'react-notifications'
// If you don't have a shipment_white.svg, you can use another icon
import plusIcon from '../../../assets/svgs/plus.svg'
import CustomFormInput from '../../../components/custom/CustomInput/custom-form-input'
import CustomFormSelect from '../../../components/custom/CustomSelect/custom-form-select'
import { RInputDeclare } from '../../../components/custom/RInputDeclare'
import GenericModal from '../../../components/generic-modal'
import postalPricingService from '../../../services/postalPricingService'

export const postalModalText = (text) => t(`components.postalModal.${text}`)

const PostalPricingModal = ({
   open,
   onClose,
   postalDetails,
   setPostalDetails,
   fetchPostalPricing,
   postalTypes,
   cities,
   short_code,
}) => {
   const [loading, setLoading] = useState(false)
   const [price, setPrice] = useState(postalDetails?.price || '')
   const [priceWithoutTax, setPriceWithoutTax] = useState(postalDetails?.price_without_tax || '')
   const [cityId, setCityId] = useState(postalDetails?.city_id || '')
   const [postalId, setPostalId] = useState(postalDetails?.postal_id || '')
   const [typeId, setTypeId] = useState(postalDetails?.type_id || 1) // Usually a category/group identifier
   const [alphaId, setAlphaId] = useState(postalDetails?.alpha_id || '')
   const [alphaDescription, setAlphaDescription] = useState(postalDetails?.alpha_description || '')
   const [notes, setNotes] = useState(postalDetails?.notes || '')

   useEffect(() => {
      if (postalDetails) {
         setPrice(postalDetails.price)
         setPriceWithoutTax(postalDetails.price_without_tax)
         setCityId(postalDetails.city_id)
         setPostalId(postalDetails.postal_id)
         setTypeId(postalDetails.type_id || 1)
         setAlphaId(postalDetails.alpha_id)
         setAlphaDescription(postalDetails.alpha_description)
         setNotes(postalDetails.notes || '')
      } else {
         setPrice('')
         setPriceWithoutTax('')
         setCityId('')
         setPostalId('')
         setTypeId(1)
         setAlphaId('')
         setAlphaDescription('')
         setNotes('')
      }
   }, [postalDetails])

   const validateForm = () => {
      if (!price || price <= 0) {
         NotificationManager.warning('Price is required and must be greater than 0!')
         return false
      }
      if (!priceWithoutTax || priceWithoutTax <= 0) {
         NotificationManager.warning('Price without tax is required and must be greater than 0!')
         return false
      }
      if (!cityId) {
         NotificationManager.warning('City is required!')
         return false
      }
      if (!postalId) {
         NotificationManager.warning('Postal type is required!')
         return false
      }
      if (!alphaId) {
         NotificationManager.warning('Alpha ID is required!')
         return false
      }
      if (!alphaDescription) {
         NotificationManager.warning('Alpha description is required!')
         return false
      }
      return true
   }

   const createPostalPricing = () => {
      if (!validateForm()) return

      setLoading(true)
      const payload = {
         price: parseFloat(price),
         price_without_tax: parseFloat(priceWithoutTax),
         city_id: parseInt(cityId),
         postal_id: parseInt(postalId),
         type_id: parseInt(typeId),
         alpha_id: alphaId,
         alpha_description: alphaDescription,
         notes: notes
      }

      postalPricingService
         .createPostalPricing(payload, { venue_short_code: short_code })
         .then(() => {
            NotificationManager.success(
               'Postal pricing has been created successfully',
               'Success',
               3000
            )
            setLoading(false)
            onClose()
            setPostalDetails(undefined)
            fetchPostalPricing()
         })
         .catch((error) => {
            setLoading(false)
            NotificationManager.error(
               error.message || 'Something went wrong!',
               'Error',
               3000
            )
         })
   }

   const updatePostalPricing = (id) => {
      if (!validateForm()) return

      setLoading(true)
      const payload = {
         price: parseFloat(price),
         price_without_tax: parseFloat(priceWithoutTax),
         alpha_id: alphaId,
         alpha_description: alphaDescription,
         notes: notes
      }

      postalPricingService
         .updatePostalPricing(id, payload, { venue_short_code: short_code })
         .then(() => {
            NotificationManager.success(
               'Postal pricing has been updated successfully',
               'Success',
               3000
            )
            setLoading(false)
            onClose()
            setPostalDetails(undefined)
            fetchPostalPricing()
         })
         .catch((error) => {
            setLoading(false)
            NotificationManager.error(
               error.message || 'Something went wrong!',
               'Error',
               3000
            )
         })
   }

   const onSubmit = () => {
      if (postalDetails) {
         updatePostalPricing(postalDetails.id)
      } else {
         createPostalPricing()
      }
   }

   const cityOptions = cities.map((city) => ({
      value: city.id,
      label: city.name,
   }))

   const postalTypeOptions = postalTypes.map((type) => ({
      value: type.id,
      label: type.name,
   }))

   const children = (
      <div className="flex flex-col pt-0 mt-2 mb-4">
         <div className="grid md:grid-cols-2 grid-cols-1 flex-col bg-white rounded-3xl gap-3 md:gap-6">
            {!postalDetails && (
               <>
                  <CustomFormSelect
                     title={postalModalText('city')}
                     value={cityId}
                     setOptionValue={setCityId}
                     options={cityOptions}
                     placeholder="Select City"
                     required={true}
                  />

                  <CustomFormSelect
                     title={postalModalText('postal_type')}
                     value={postalId}
                     setOptionValue={setPostalId}
                     options={postalTypeOptions}
                     placeholder="Select Postal Type"
                     required={true}
                  />
               </>
            )}

            <CustomFormInput
               title={postalModalText('price')}
               required={true}
               type="number"
               value={price}
               placeholder={postalModalText('enter_price')}
               onChange={(e) => setPrice(e.target.value)}
            />

            <CustomFormInput
               title={postalModalText('price_without_tax')}
               required={true}
               type="number"
               value={priceWithoutTax}
               placeholder={postalModalText('enter_price_without_tax')}
               onChange={(e) => setPriceWithoutTax(e.target.value)}
            />
         </div>

         <div className="grid md:grid-cols-2 grid-cols-1 flex-col bg-white rounded-3xl gap-3 md:gap-6 mt-4">
            <CustomFormInput
               title={postalModalText('alpha_id')}
               required={true}
               value={alphaId}
               placeholder={postalModalText('enter_alpha_id')}
               onChange={(e) => setAlphaId(e.target.value)}
            />

            <CustomFormInput
               title={postalModalText('alpha_description')}
               required={true}
               value={alphaDescription}
               placeholder={postalModalText('enter_alpha_description')}
               onChange={(e) => setAlphaDescription(e.target.value)}
            />
         </div>

         <RInputDeclare
            value={postalModalText('notes')}
            className="!mt-4 !mb-2 !text-black1 font-medium text-base"
         />
         <TextareaAutosize
            minRows={3}
            value={notes}
            placeholder={postalModalText('enter_notes')}
            onChange={(e) => setNotes(e.target.value)}
            className="border outline-none p-2"
         />
      </div>
   )

   return (
      <GenericModal
         {...{ open, onClose, onSubmit, children, loading }}
         icon={plusIcon} // Using plusIcon as fallback if you don't have shipmentIcon
         title={
            postalDetails
               ? postalModalText('edit_postal_pricing')
               : postalModalText('new_postal_pricing')
         }
      />
   )
}

export default PostalPricingModal